import { Link } from "react-router-dom";
import {
  ArrowRight,
  AlertTriangle,
  Layers,
  EyeOff,
  FileText,
  Network,
  Boxes,
  RotateCw,
  CheckCircle2,
  ShieldCheck,
  TrendingDown,
  Users2,
  Eye,
  Activity,
  Wallet,
  Truck,
  ScrollText,
  Workflow,
  CircleDollarSign,
  PackageCheck,
  Building2,
  Microscope,
  Factory,
  GitBranch,
} from "lucide-react";
import PiSymbol from "@/components/PiSymbol";
import AnimatedCounter from "@/components/AnimatedCounter";
import RhombusGrid from "@/components/RhombusGrid";
import RotatingText from "@/components/RotatingText";
import {
  HERO_BG,
  VERTICALS,
  STATS,
  PARTNERS,
  INVESTORS,
} from "@/data/brand";

const FIDM_STEPS = [
  {
    icon: FileText,
    title: "Fragment",
    body:
      "You share your BOM or part requirement — any volume, any category mix, any complexity. We accept what others reject.",
  },
  {
    icon: Network,
    title: "Aggregate",
    body:
      "Pie fragments your demand intelligently across our network of 241+ qualified manufacturers. Your small order is pooled with similar category demand to achieve manufacturer-level pricing.",
  },
  {
    icon: Boxes,
    title: "Consolidate",
    body:
      "All parts — across Fasteners, Rubber, Electronics, Decals, and Injection Moulding — are quality-inspected, kitted if required, and dispatched as a single consolidated shipment with full PPAP documentation.",
  },
  {
    icon: RotateCw,
    title: "Repeat On-Demand",
    body:
      "FIDM is not a one-time transaction. We build a living demand model around your production schedule. Reorder on demand, no MOQ, no stock pressure.",
  },
];

const FIDM_UNLOCKS = [
  {
    icon: TrendingDown,
    title: "BOM Tail Spend Reduction",
    desc: "20–30% cost savings on B&C Class parts",
  },
  {
    icon: Users2,
    title: "Vendor Consolidation",
    desc: "Eliminate 30+ suppliers, one partner",
  },
  {
    icon: Activity,
    title: "Zero Line Loss",
    desc: "Safety stock + KANBAN + Watch Tower = 100% OTIF commitment",
  },
  {
    icon: ShieldCheck,
    title: "Quality Assurance",
    desc: "PPAP Level 5, QPPM tracking, 3rd party testing, CAPA/8D ownership",
  },
  {
    icon: Eye,
    title: "Full Cost Transparency",
    desc: "Should-cost modelling, benchmarking, RM indexation — no black box",
  },
  {
    icon: PackageCheck,
    title: "No MOQ Pressure",
    desc: "Buy exactly what you need, when you need it",
  },
];

const CHALLENGES = [
  {
    title: "Supplier Fragmentation",
    problem:
      "Managing 30–50 different vendors for B&C Class parts consumes enormous procurement bandwidth and creates risk at every node.",
    answer: "Single-partner aggregation via FIDM — One vendor. All categories.",
    icon: Layers,
  },
  {
    title: "High Inventory Carrying Cost",
    problem:
      "Overstocking due to MOQ pressure leads to dead inventory, blocked capital, and wasted factory floor space.",
    answer:
      "FIDM on-demand ordering + 2-BIN KANBAN system + VMI — buy only what you need.",
    icon: Wallet,
  },
  {
    title: "Low Volume Pricing Penalty",
    problem:
      "Small-batch buyers pay 15–40% more per part than large OEMs for identical specifications simply due to volume.",
    answer:
      "Demand aggregation across Pie's client network unlocks OEM-level pricing for small volume buyers.",
    icon: CircleDollarSign,
  },
  {
    title: "Quality Inconsistency (High QPPM)",
    problem:
      "B&C Class suppliers rarely maintain PPAP, process audits, or CAPA documentation — quality is unpredictable and rejections go untracked.",
    answer:
      "Quality 1st Policy — PPAP Level 5, PDIR, CAPA/8D, process audits, 3rd party testing — owned by Pie's 17-member quality team.",
    icon: ShieldCheck,
  },
  {
    title: "Delivery Failures (OTIF Impact)",
    problem:
      "Supplier delays in B&C Class parts cause line stoppages — a single missing fastener or seal can halt an entire production line.",
    answer:
      "Supply Chain Watch Tower + Safety Stock + KANBAN = Zero Line Loss commitment.",
    icon: Truck,
  },
  {
    title: "Zero Cost Transparency",
    problem:
      "Buyers have no idea if they are being fairly priced. No should-cost model, no RM benchmarking, no price history.",
    answer:
      "Phygital Costing Framework — Should Cost Modelling, Delta Lake Benchmarking (Bronze/Silver/Gold), RM Indexation. Full transparency.",
    icon: Eye,
  },
  {
    title: "Opaque Manufacturing Process",
    problem:
      "Buyers have no visibility into their supplier's factory, process capability, or sub-tier supplier network. Change happens without notice.",
    answer:
      "Supplier Compass (60+ data points, real-time risk mapping) + PPAP Level 5 audit access + quarterly process audits by Pie QA.",
    icon: EyeOff,
  },
  {
    title: "Ineffective Change Management",
    problem:
      "Engineering change notices (ECN/PCN) from the buyer rarely reach the supplier in time — leading to wrong parts and costly rework.",
    answer:
      "Formal ECN/PCN ownership by Pie's quality team — every change communicated in advance. Document retention: 3 years.",
    icon: GitBranch,
  },
];

const SOLUTIONS = [
  {
    title: "BOM Management",
    desc: "Production-linked direct part procurement, E2E Design-to-Deliver",
    icon: ScrollText,
  },
  {
    title: "MRO",
    desc: "Catalog-based one-stop ecommerce for all indirect purchases",
    icon: Boxes,
  },
  {
    title: "Kanban Solutions",
    desc: "2-BIN/KANBAN, VMI, inventory optimisation at the line",
    icon: Workflow,
  },
  {
    title: "Kitting Solution",
    desc: "Prompt delivery, agile supply chain, fewer errors, better productivity",
    icon: PackageCheck,
  },
  {
    title: "Procurement as a Service",
    desc: "Full procurement management for larger enterprises",
    icon: Factory,
  },
  {
    title: "Consulting",
    desc: "No Cure, No Pay Policy — pay only if we deliver tangible savings",
    icon: Microscope,
  },
];

export default function Home() {
  return (
    <main data-testid="home-page" className="overflow-x-hidden">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100vh] flex items-center"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt="Industrial factory at night"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/70 via-[#0a0a0a]/85 to-[#0a0a0a]" />
          <div className="absolute inset-0 grid-backdrop opacity-25" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-8">
                <PiSymbol size={48} />
                <span className="section-label !text-[#39FF14]">
                  Incubated at IIM Mumbai
                </span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] max-w-3xl">
                <span className="text-white">One Partner.</span>
                <br />
                <RotatingText
                  data-testid="hero-rotating-text"
                  phrases={[
                    "Authorized Distributorship",
                    "Qualified Manufacturing Partners",
                    "Transparent Supply Chain",
                  ]}
                  intervalMs={2000}
                  className="text-[#39FF14]"
                />
                <br />
                <span className="text-white">Zero Vendor Chaos.</span>
              </h1>

              <p className="mt-8 text-base md:text-lg max-w-2xl text-white/75 leading-relaxed">
                Pie Solutions (π) is India's 360° Design-to-Deliver aggregator for
                Future Factories — eliminating 30+ vendors, optimising BOM tail
                spend by{" "}
                <span className="text-[#39FF14] font-semibold">20–30%</span>, and
                ensuring{" "}
                <span className="text-[#39FF14] font-semibold">
                  Zero Line Loss
                </span>
                .
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="#fidm"
                  data-testid="hero-cta-fidm"
                  className="btn-primary"
                >
                  Discover FIDM Solution <ArrowRight size={16} />
                </a>
                <Link
                  to="/contact"
                  data-testid="hero-cta-bom"
                  className="btn-ghost"
                >
                  Share Your BOM <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <RhombusGrid />
            </div>
          </div>

          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
            {[
              ["20–30%", "BOM Tail Spend Cut"],
              ["30+", "Vendors Eliminated"],
              ["100%", "OTIF Committed"],
              ["241+", "Manufacturers"],
            ].map(([k, v]) => (
              <div key={v} className="border-l border-[#39FF14]/40 pl-4">
                <div className="font-display text-3xl md:text-4xl font-bold text-[#39FF14]">
                  {k}
                </div>
                <div className="text-xs uppercase tracking-[0.22em] text-white/60 mt-1">
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM: Low Volume High Cost */}
      <section
        data-testid="problem-section"
        className="relative py-24 md:py-32 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label !text-[#DC143C]">
              The Real Problem
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              Small Volumes Shouldn't Mean{" "}
              <span className="text-[#DC143C]">Big Penalties</span>
            </h2>
            <p className="text-white/70 mt-5 text-base md:text-lg leading-relaxed">
              Here's the brutal reality for manufacturers with low production
              volumes or high SKU complexity — and why the current supply chain
              system is failing them.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: "The Fragmentation Problem",
                icon: Layers,
                points: [
                  "BOMs with 100+ line items spread across 5 different categories: fasteners, rubber, electronics, decals and moulded parts",
                  "Each category needs a different vendor with its own MOQ, lead time, and quality standard",
                  "Procurement teams managing 30–50 supplier relationships just for B&C class parts",
                  "Coordination, PO management, follow-up & vendor development cost is NEVER counted in your COGS",
                ],
              },
              {
                title: "The Low Volume Penalty",
                icon: AlertTriangle,
                points: [
                  "Suppliers prioritise large-volume buyers — small buyers get higher per-unit prices and longer lead times",
                  "MOQ rejections force small buyers to over-order, creating dead inventory and cash flow strain",
                  "Tooling and die investment forced on buyers even for trial or low-volume requirements",
                  "Small batch manufacturers end up paying 15–40% more per part than high-volume OEMs",
                ],
              },
              {
                title: "The Visibility Gap",
                icon: EyeOff,
                points: [
                  "Opaque manufacturing process — no visibility of where parts are at any stage",
                  "No should-cost model, no benchmarking, no RM indexation",
                  "Quality is inconsistent — no structured PPAP, CAPA, or QPPM tracking",
                  "Change management is informal — ECN/PCN happens without buyer notification",
                ],
              },
            ].map((col) => (
              <div
                key={col.title}
                data-testid={`problem-card-${col.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="glass-card p-7 relative"
                style={{
                  borderColor: "rgba(220,20,60,0.18)",
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-11 h-11 rounded-md bg-[#DC143C]/10 border border-[#DC143C]/40 flex items-center justify-center">
                    <col.icon size={20} className="text-[#DC143C]" />
                  </div>
                  <div className="text-xs uppercase tracking-[0.3em] font-display text-white/40">
                    Pain Point
                  </div>
                </div>
                <h3 className="font-display text-xl font-semibold mb-4">
                  {col.title}
                </h3>
                <ul className="space-y-3">
                  {col.points.map((p) => (
                    <li
                      key={p}
                      className="text-sm text-white/70 leading-relaxed flex gap-2"
                    >
                      <span className="text-[#DC143C] mt-1.5 inline-block w-1 h-1 rounded-full bg-[#DC143C] flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 border border-[#DC143C]/30 bg-[#DC143C]/5 rounded-xl p-8 md:p-10">
            <div className="font-display text-2xl md:text-3xl font-semibold leading-snug">
              The problem isn't your production volume.{" "}
              <span className="text-[#DC143C]">
                The problem is that the supply chain was never designed for you.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FIDM SOLUTION — flagship */}
      <section
        id="fidm"
        data-testid="fidm-section"
        className="relative py-28 md:py-40 border-t border-white/5"
      >
        <div className="absolute inset-0 grid-backdrop opacity-30 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#39FF14]/60 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <PiSymbol size={36} />
            <span className="section-label">Our Proprietary Solution</span>
          </div>

          <h2 className="font-display text-5xl md:text-7xl font-bold leading-[0.95]">
            Introducing <span className="text-gradient-green">FIDM™</span>
          </h2>
          <div className="mt-4 font-display text-2xl md:text-3xl font-semibold text-white/90">
            Fragment in Demand for Manufacturing
          </div>

          <div className="mt-12 max-w-4xl border-l-4 border-[#39FF14] pl-8 md:pl-10 py-2">
            <p className="text-white/85 text-base md:text-lg leading-relaxed">
              <span className="text-[#39FF14] font-display font-bold text-2xl">
                FIDM
              </span>{" "}
              — Fragment in Demand for Manufacturing — is Pie Solutions'
              proprietary procurement framework designed specifically for
              manufacturers who operate at low production volumes or manage
              high SKU complexity across multiple part categories.
            </p>
            <p className="text-white/70 mt-5 text-base leading-relaxed">
              Instead of forcing small manufacturers to meet MOQs they don't
              need, FIDM fragments the demand intelligently across Pie's entire
              supplier and manufacturer network — aggregating small orders from
              multiple clients in the same category, enabling each client to
              receive exactly what they need, when they need it, at a price
              previously only available to large-volume OEMs.
            </p>
            <p className="text-white/60 mt-5 text-base leading-relaxed italic">
              FIDM is not a product. It is not a catalogue. It is an end-to-end
              procurement operating system — built around your actual demand
              pattern, not a supplier's MOQ.
            </p>
          </div>

          {/* Steps */}
          <div className="mt-20">
            <div className="section-label mb-6">How FIDM Works</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 relative">
              {FIDM_STEPS.map((s, i) => (
                <div
                  key={s.title}
                  data-testid={`fidm-step-${i + 1}`}
                  className="glass-card p-7 relative group"
                >
                  <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-[#0a0a0a] border-2 border-[#39FF14] flex items-center justify-center font-display font-bold text-[#39FF14] shadow-[0_0_18px_rgba(57,255,20,0.4)]">
                    {i + 1}
                  </div>
                  <s.icon size={26} className="text-[#39FF14] mb-4" />
                  <h3 className="font-display text-2xl font-bold mb-3 uppercase tracking-wide">
                    {s.title}
                  </h3>
                  <p className="text-sm text-white/65 leading-relaxed">
                    {s.body}
                  </p>
                  {i < FIDM_STEPS.length - 1 && (
                    <ArrowRight
                      size={20}
                      className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 text-[#39FF14]/60"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Unlocks */}
          <div className="mt-24">
            <div className="section-label mb-6">What FIDM Unlocks</div>
            <h3 className="font-display text-3xl md:text-4xl font-bold mb-10">
              Six measurable wins for your factory floor.
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {FIDM_UNLOCKS.map((u) => (
                <div
                  key={u.title}
                  data-testid={`fidm-unlock-${u.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="glass-card p-7"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-md bg-[#39FF14]/10 border border-[#39FF14]/40 flex items-center justify-center flex-shrink-0">
                      <u.icon size={20} className="text-[#39FF14]" />
                    </div>
                    <div>
                      <h4 className="font-display text-lg font-semibold mb-1.5">
                        {u.title}
                      </h4>
                      <p className="text-sm text-white/65 leading-relaxed">
                        {u.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stamp */}
          <div className="mt-16 flex flex-wrap items-center gap-6 border border-[#39FF14]/30 bg-[#39FF14]/5 rounded-xl p-7">
            <PiSymbol size={56} />
            <div className="font-display text-lg md:text-xl font-semibold leading-snug">
              Designed for Small Batch. Built for Complexity.{" "}
              <span className="text-[#39FF14]">Backed by IIM Mumbai.</span>
            </div>
          </div>
        </div>
      </section>

      {/* CHALLENGES — 8 cards */}
      <section
        data-testid="challenges-section"
        className="relative py-24 md:py-32 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label">Challenges We Solve</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              The 8 Supply Chain Challenges{" "}
              <span className="text-[#DC143C]">Killing Your Profitability</span>
            </h2>
            <p className="text-white/70 mt-5 text-base md:text-lg leading-relaxed">
              Manufacturing companies across India are losing money every day
              due to these unresolved B&C Class procurement problems. Pie
              Solutions was built to eliminate every single one.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {CHALLENGES.map((c, i) => (
              <div
                key={c.title}
                data-testid={`challenge-card-${i + 1}`}
                className="glass-card p-6 flex flex-col"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-10 h-10 rounded-md bg-[#DC143C]/10 border border-[#DC143C]/30 flex items-center justify-center">
                    <c.icon size={18} className="text-[#DC143C]" />
                  </div>
                  <div className="font-mono text-xs text-white/40">
                    0{i + 1}
                  </div>
                </div>
                <h3 className="font-display text-lg font-semibold mb-3">
                  {c.title}
                </h3>
                <p className="text-sm text-white/60 leading-relaxed">
                  {c.problem}
                </p>
                <div className="mt-5 pt-4 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#39FF14] mb-2 font-display">
                    Pie's Answer
                  </div>
                  <p className="text-sm text-white/85 leading-relaxed">
                    {c.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE — 3 PILLARS */}
      <section
        data-testid="value-section"
        className="relative py-24 md:py-32 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label">Value Unlocking</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              How Pie Unlocks Measurable Value in Your Supply Chain
            </h2>
            <p className="text-white/70 mt-5 text-base md:text-lg">
              We don't just supply parts — we improve your Supply Chain KPIs on
              Quality, Cost, and Delivery. Every engagement is designed to
              deliver measurable, auditable results.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-5">
            {[
              {
                tag: "Cost Value",
                title: "Reduce What You Spend",
                color: "#39FF14",
                points: [
                  "20–30% BOM Tail Spend Optimisation",
                  "Should Cost Modelling eliminates supplier pricing opacity",
                  "Demand aggregation gives small buyers OEM-level leverage",
                  "RM Indexation prevents arbitrary price escalations",
                  "\"No Cure, No Pay\" Consulting — pay only if you save",
                ],
              },
              {
                tag: "Quality Value",
                title: "Eliminate What You Reject",
                color: "#00BFFF",
                points: [
                  "QPPM improvement through structured PPAP & process audits",
                  "17-member dedicated quality team (20–40 yrs experience)",
                  "CAPA/8D analysis fully owned by Pie — buyer gets reports, not excuses",
                  "3rd party NABL lab testing (160 NABL labs in network)",
                  "Warranty & Indemnity Bond signed by Pie Solutions",
                ],
              },
              {
                tag: "Delivery Value",
                title: "Protect Your Line",
                color: "#39FF14",
                points: [
                  "2-BIN / KANBAN system prevents stockouts at the line",
                  "Watch Tower: real-time stock visibility (supplier, warehouse, transit)",
                  "Safety stock at 28 dedicated warehouses",
                  "Design for Logistics — packaging optimisation reduces damage",
                  "OTIF as a committed metric — not a best-effort target",
                ],
              },
            ].map((p) => (
              <div
                key={p.tag}
                data-testid={`pillar-${p.tag.toLowerCase().replace(/\s+/g, "-")}`}
                className="glass-card p-8 relative"
              >
                <div
                  className="text-xs uppercase tracking-[0.32em] font-display mb-4"
                  style={{ color: p.color }}
                >
                  {p.tag}
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold mb-6">
                  {p.title}
                </h3>
                <ul className="space-y-3">
                  {p.points.map((pt) => (
                    <li
                      key={pt}
                      className="flex gap-3 text-sm text-white/75 leading-relaxed"
                    >
                      <CheckCircle2
                        size={16}
                        style={{ color: p.color }}
                        className="flex-shrink-0 mt-0.5"
                      />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* STATS BAR */}
          <div className="mt-20">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-white/10 rounded-xl overflow-hidden bg-[#0f0f0f]">
              {STATS.map((s, i) => (
                <div
                  key={s.label}
                  data-testid={`stat-${i}`}
                  className="p-6 border-r border-b md:border-b-0 border-white/10 last:border-r-0 text-center"
                >
                  <div className="font-display text-3xl md:text-4xl font-bold text-[#39FF14]">
                    <AnimatedCounter value={s.value} />
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 mt-2">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTIONS SUITE */}
      <section
        data-testid="solutions-section"
        className="relative py-24 md:py-32 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label">Solutions Suite</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              1-Stop Tailor-Made Solutions for Every Need
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SOLUTIONS.map((s, i) => (
              <div
                key={s.title}
                data-testid={`solution-card-${i + 1}`}
                className="glass-card p-7"
              >
                <div className="w-12 h-12 rounded-md bg-[#39FF14]/10 border border-[#39FF14]/40 flex items-center justify-center mb-5">
                  <s.icon size={22} className="text-[#39FF14]" />
                </div>
                <h3 className="font-display text-xl font-semibold mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-white/65 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VERTICALS — with real product photography */}
      <section
        data-testid="verticals-section"
        className="relative py-24 md:py-32 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label">Our 5 Verticals</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              Five categories. <span className="text-[#39FF14]">One π.</span>
            </h2>
            <p className="text-white/70 mt-5 text-base md:text-lg">
              Every vertical backed by domain experts, IATF-certified
              manufacturers, and Pie's Quality 1st Policy.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VERTICALS.map((v, i) => (
              <div
                key={v.id}
                data-testid={`vertical-card-${v.id}`}
                className={`relative group overflow-hidden rounded-xl border border-white/10 hover:border-[#39FF14]/60 transition-all duration-500 ${
                  i === 0 ? "lg:col-span-2 lg:row-span-1" : ""
                }`}
              >
                <div
                  className="relative h-[360px] md:h-[420px] w-full overflow-hidden"
                >
                  <img
                    src={v.image}
                    alt={v.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent to-[#0a0a0a]/40 group-hover:from-[#39FF14]/10 transition-all duration-500" />

                  <div className="absolute inset-0 p-7 flex flex-col justify-end">
                    <div className="text-[10px] uppercase tracking-[0.32em] font-display text-[#39FF14] mb-2">
                      Vertical 0{i + 1}
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl font-bold mb-2">
                      {v.name}
                    </h3>
                    <p className="text-sm text-white/70 italic mb-4">
                      {v.tagline}
                    </p>
                    <ul className="space-y-1.5">
                      {v.sub.map((s) => (
                        <li
                          key={s}
                          className="text-xs md:text-sm text-white/80 flex gap-2"
                        >
                          <span className="text-[#39FF14] mt-1.5 inline-block w-1 h-1 rounded-full bg-[#39FF14] flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PIE — 3 pillar */}
      <section
        data-testid="why-section"
        className="relative py-24 md:py-32 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label">Why Pie Solutions</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              Why <span className="text-[#39FF14]">We're Different</span>
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-5">
            {[
              {
                tag: "Domain Expertise",
                title: "PhD-level engineers",
                body: "20–40 years of cross-industry experience across automotive, industrial, chemicals, and polymers.",
                icon: Users2,
              },
              {
                tag: "Technology",
                title: "Supplier Compass + Watch Tower",
                body: "60-tool value toolbox, proprietary patented tech for risk mapping, costing, and supply chain visibility.",
                icon: Activity,
              },
              {
                tag: "Business Solution",
                title: "5V Framework",
                body: "Value | Variety | Visibility | Velocity | Veracity — engineered into every engagement.",
                icon: Building2,
              },
            ].map((p) => (
              <div
                key={p.tag}
                data-testid={`why-pillar-${p.tag.toLowerCase().replace(/\s+/g, "-")}`}
                className="glass-card p-8"
              >
                <p.icon size={24} className="text-[#39FF14] mb-5" />
                <div className="text-xs uppercase tracking-[0.3em] font-display text-white/50 mb-2">
                  {p.tag}
                </div>
                <h3 className="font-display text-2xl font-bold mb-4">
                  {p.title}
                </h3>
                <p className="text-sm text-white/65 leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTORS */}
      <section
        data-testid="investors-section"
        className="relative py-24 md:py-32 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="section-label">Backed By</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4">
              Manufacturing Stalwarts &{" "}
              <span className="text-[#39FF14]">Government of India</span>
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {INVESTORS.map((p, i) => (
              <div
                key={p.name}
                data-testid={`investor-card-${i + 1}`}
                className="glass-card p-6 flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#39FF14]/30 to-[#00BFFF]/30 border border-[#39FF14]/40 flex items-center justify-center flex-shrink-0">
                  <span className="font-display text-xl font-bold text-[#39FF14]">
                    {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                </div>
                <div>
                  <div className="font-display text-lg font-semibold">
                    {p.name}
                  </div>
                  <div className="text-xs text-white/55 mt-1 leading-relaxed">
                    {p.title}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section
        data-testid="partners-section"
        className="relative py-20 border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="section-label">Technology & Research Partners</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            {PARTNERS.map((p) => (
              <div
                key={p}
                data-testid={`partner-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="font-display text-lg md:text-xl uppercase tracking-[0.18em] text-white/40 hover:text-[#39FF14] transition-colors"
              >
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="final-cta-section"
        className="relative py-28 md:py-40 border-t border-white/5"
      >
        <div className="absolute inset-0 grid-backdrop opacity-40 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <PiSymbol size={64} />
          <h2 className="font-display text-4xl md:text-6xl font-bold mt-8 leading-tight">
            Ready to Eliminate{" "}
            <span className="text-[#39FF14]">30+ Vendors?</span>
          </h2>
          <p className="text-white/70 mt-6 text-base md:text-lg max-w-2xl mx-auto">
            Share your BOM. Get a consolidated quote in 48 hours. No commitment
            needed.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-share-bom"
              className="btn-primary"
            >
              Share Your BOM Now <ArrowRight size={16} />
            </Link>
            <Link to="/contact" data-testid="cta-book-call" className="btn-ghost">
              Book a Call <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
